import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Link,
  Menu,
  Tabs,
  Text,
  VStack
} from '@chakra-ui/react';
import {
  Download,
  Envelope,
  LinkedinLogo,
  Phone,
  Calendar,
  User,
  Megaphone,
  HourglassHigh,
  Plus,
  Warning,
  FileDoc
} from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiAddCandidateToJob, apiGetCandidateProfile } from '@/api/services';
import Resume from '@/components/data-display/Resume';
import { Avatar } from '@/components/ui/avatar';
import { Skeleton, SkeletonText, SkeletonCircle } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import { Candidate } from '@/types/Candidate';
import { getJobCandidateStatusLabel, JobCandidateStatus, ResumeType } from '@/types/enums';
import { getCandidateExperienceLevelLabel } from '@/types/enums/CandidateExperienceLevel';
import AddCandidateToJobDialog from './AddCandidateToJobDialog';
import EditCandidateDialog from './EditCandidateDialog';
import { BreadcrumbNavigation } from './components/BreadcrumbNavigation';
import { CandidateOverviewSection } from './CandidateOverviewSection';
import InterviewAnalysisIndex from './InterviewAnalysisSection';
import { getCandidateFullName, exportResumeToDocx } from '@/utils';

export default function CandidateProfile() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [addingToJob, setAddingToJob] = useState<string | null>(null);
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const resumeRef = useRef<HTMLDivElement | null>(null);
  const [exportingDocx, setExportingDocx] = useState(false);

  useEffect(() => {
    const loadCandidate = async () => {
      if (!candidateId) {
        setError(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetchAPI<Candidate>({
          request: () => apiGetCandidateProfile({ candidateId })
        });

        if (response.success) {
          setCandidate(response.data);
        } else {
          console.error('Failed to fetch candidate:', response.message);
          setError(true);
        }
      } catch (error) {
        console.error('Failed to fetch candidate:', error);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadCandidate();
  }, [candidateId]);

  const updateCandidateField = useCallback((fields: Partial<Candidate>) => {
    setCandidate((prevCandidate) => (prevCandidate ? { ...prevCandidate, ...fields } : null));
  }, []);

  const loadCandidateData = useCallback(async () => {
    if (!candidateId) return;

    try {
      const response = await fetchAPI<Candidate>({
        request: () => apiGetCandidateProfile({ candidateId })
      });

      if (response.success) {
        setCandidate(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh candidate data:', error);
    }
  }, [candidateId]);

  // Auto-refresh effect for pipeline jobs with New status
  useEffect(() => {
    if (!candidate) return;

    const hasNewJobs = candidate.pipeline_jobs.some(
      (job) => job.candidate_status === JobCandidateStatus.New
    );

    if (hasNewJobs) {
      intervalRef.current = setInterval(() => {
        loadCandidateData();
      }, 5000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [candidate, loadCandidateData]);

  const handleExportFormattedResumeDocx = async () => {
    if (!candidate?.resume_data) return;
    setExportingDocx(true);
    try {
      await exportResumeToDocx(
        candidate.resume_data,
        `${candidate.first_name} ${candidate.last_name} Resume.docx`
      );
    } finally {
      setExportingDocx(false);
    }
  };

  const handleAddCandidateToJob = async (jobId: string) => {
    if (!candidate) return;

    setAddingToJob(jobId);
    try {
      const response = await fetchAPI({
        request: () =>
          apiAddCandidateToJob({
            jobId,
            payload: { candidate_id: candidate.candidate_id }
          })
      });

      if (response.success) {
        // Refresh candidate data to get updated pipeline_jobs
        await loadCandidateData();
        // Interview data will be automatically loaded via the useEffect
      } else {
        console.error('Failed to add candidate to job:', response.message);
      }
    } catch (error) {
      console.error('Failed to add candidate to job:', error);
    } finally {
      setAddingToJob(null);
    }
  };

  if (isLoading) {
    return (
      <Flex w="100%" flexDir="column" p="40px 48px">
        <Flex justifyContent="space-between" alignItems="center" mb="24px">
          <SkeletonText noOfLines={1} w="200px" />
          <Skeleton borderRadius="8px" h="40px" w="150px" />
        </Flex>
        <Flex justifyContent="space-between" mb="32px">
          <Box
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="12px"
            flex="2"
            h="280px"
            p="32px"
          >
            <Flex alignItems="center" gap="24px">
              <SkeletonCircle size="120px" />
              <VStack align="flex-start" gap="16px" flex="1">
                <SkeletonText noOfLines={3} w="80%" />
              </VStack>
            </Flex>
          </Box>
          <Box w="24px" />
          <Box
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="12px"
            flex="1"
            h="280px"
            p="32px"
          >
            <SkeletonText noOfLines={6} />
          </Box>
        </Flex>
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          flex="1"
          h="400px"
          p="32px"
        >
          <SkeletonText noOfLines={10} />
        </Box>
      </Flex>
    );
  }

  if (error || !candidate) {
    return (
      <Flex w="100%" flexDir="column" p="40px 48px">
        <Flex>
          <BreadcrumbNavigation onNavigate={navigate} />
        </Flex>
        <Flex direction="column" justify="center" align="center" height="400px">
          <Text fontSize="20px" fontWeight="bold" color="error.700" textAlign="center" mb="8px">
            Unable to load candidate profile.
          </Text>
          <Text fontSize="16px" color="error.700" textAlign="center">
            Please try again later or contact support for assistance.
          </Text>
        </Flex>
      </Flex>
    );
  }

  const getLastWorkExperience = () => {
    return candidate.resume_data?.work_experiences?.[0] || null;
  };

  const getCreatedDateFromNow = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Flex w="100%" flexDir="column" p="40px 48px">
      {/* Header with Navigation */}
      <Flex justifyContent="space-between" alignItems="center" mb="24px">
        <BreadcrumbNavigation
          candidateId={candidateId}
          candidateName={getCandidateFullName(candidate.first_name, candidate.last_name)}
          onNavigate={navigate}
        />
        <EditCandidateDialog
          candidateId={candidate.candidate_id}
          firstName={candidate.first_name}
          lastName={candidate.last_name}
          email={candidate.email}
          phone={candidate.phone}
          linkedin={candidate.linkedin}
          onUpdateCandidate={(first_name, last_name, email, phone, linkedin) =>
            updateCandidateField({ first_name, last_name, email, phone, linkedin })
          }
        />
      </Flex>

      {/* Main Profile Section */}
      <Flex justifyContent="space-between" mb="32px">
        {/* Left: Main Profile Card */}
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          flex="2"
          p="32px"
          boxShadow="sm"
          _hover={{ boxShadow: 'md' }}
          transition="all 0.2s ease-in-out"
        >
          <Flex alignItems="center" gap="32px">
            <Avatar size="2xl" name={candidate.first_name} />
            <VStack align="flex-start" gap="16px" flex="1">
              <Badge
                bg="primary.100"
                color="primary.900"
                px="12px"
                py="4px"
                borderRadius="full"
                fontSize="12px"
                fontWeight="semibold"
              >
                {candidate.unique_id}
              </Badge>
              <Box>
                <Text fontSize="32px" fontWeight="bold" color="neutrals.900" mb="4px">
                  {getCandidateFullName(candidate.first_name, candidate.last_name)}
                </Text>
                <Text fontSize="16px" color="neutrals.700" mb="8px">
                  {getLastWorkExperience()
                    ? `${getLastWorkExperience()?.job_role} @ ${
                        getLastWorkExperience()?.company_name
                      }`
                    : 'Candidate Profile'}
                </Text>
              </Box>

              <HStack gap="16px">
                {candidate.email && (
                  <Button
                    size="sm"
                    variant="ghost"
                    bg="primary.50"
                    color="primary.900"
                    borderRadius="12px"
                    onClick={() => window.open(`mailto:${candidate.email}`)}
                    _hover={{ bg: 'primary.100' }}
                  >
                    <Envelope size={18} />
                    {candidate.email}
                  </Button>
                )}
                {candidate.linkedin && (
                  <Link
                    href={
                      candidate.linkedin.startsWith('http')
                        ? candidate.linkedin
                        : `https://${candidate.linkedin}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      bg="blue.50"
                      color="blue.700"
                      borderRadius="12px"
                      _hover={{ bg: 'blue.100' }}
                    >
                      <LinkedinLogo size={18} />
                      LinkedIn
                    </Button>
                  </Link>
                )}
                {candidate.phone && (
                  <Button
                    size="sm"
                    variant="ghost"
                    bg="neutrals.100"
                    color="neutrals.700"
                    borderRadius="12px"
                    onClick={() => window.open(`tel:${candidate.phone}`)}
                    _hover={{ bg: 'neutrals.200' }}
                  >
                    <Phone size={18} />
                    {candidate.phone}
                  </Button>
                )}
              </HStack>
            </VStack>
          </Flex>
        </Box>

        <Box w="24px" />

        {/* Right: Quick Info Card */}
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          flex="1"
          p="16px 32px"
          boxShadow="sm"
          _hover={{ boxShadow: 'md' }}
          transition="all 0.2s ease-in-out"
        >
          <VStack align="flex-start" gap="16px">
            <Text fontSize="18px" fontWeight="semibold" color="neutrals.700">
              Quick Info
            </Text>
            <VStack align="flex-start" gap="20px" w="100%">
              <Flex gap="24px">
                <HStack gap="16px" w="120px">
                  <Calendar size={20} color="#6B7280" />
                  <Box>
                    <Text fontSize="14px" color="neutrals.600" mb="2px">
                      Joined
                    </Text>
                    <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                      {new Date(candidate.created_at * 1000).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Text>
                  </Box>
                </HStack>

                <HStack gap="16px">
                  <User size={20} color="#6B7280" />
                  <Box>
                    <Text fontSize="14px" color="neutrals.600" mb="2px">
                      Experience Level
                    </Text>
                    <Text fontSize="14px" color="neutrals.500" fontWeight="medium">
                      {candidate.experience_level
                        ? getCandidateExperienceLevelLabel(candidate.experience_level)
                        : 'Not Applied'}
                    </Text>
                  </Box>
                </HStack>
              </Flex>

              <Flex gap="24px">
                <HStack gap="16px" w="120px">
                  <Megaphone size={24} color="#6B7280" />
                  <Box w="100%">
                    <Text fontSize="14px" color="neutrals.600" mb="8px">
                      Job Matches
                    </Text>
                    {candidate.match_jobs.length > 0 ? (
                      <Badge
                        bg="green.100"
                        color="green.800"
                        px="8px"
                        py="2px"
                        borderRadius="full"
                        fontSize="12px"
                        fontWeight="medium"
                      >
                        {candidate.match_jobs.length}{' '}
                        {candidate.match_jobs.length === 1 ? 'Match' : 'Matches'}
                      </Badge>
                    ) : (
                      <Badge
                        bg="neutrals.100"
                        color="neutrals.600"
                        px="8px"
                        py="2px"
                        borderRadius="full"
                        fontSize="12px"
                        fontWeight="medium"
                      >
                        No Matches
                      </Badge>
                    )}
                  </Box>
                </HStack>

                <HStack gap="16px">
                  <HourglassHigh size={24} color="#6B7280" />
                  <Box w="100%">
                    <Text fontSize="14px" color="neutrals.600" mb="8px">
                      Pipeline Jobs
                    </Text>
                    {candidate.pipeline_jobs.length > 0 ? (
                      <Badge
                        bg="blue.100"
                        color="blue.800"
                        px="8px"
                        py="2px"
                        borderRadius="full"
                        fontSize="12px"
                        fontWeight="medium"
                      >
                        {candidate.pipeline_jobs.length}{' '}
                        {candidate.pipeline_jobs.length === 1 ? 'Job' : 'Jobs'}
                      </Badge>
                    ) : (
                      <Badge
                        bg="neutrals.100"
                        color="neutrals.600"
                        px="8px"
                        py="2px"
                        borderRadius="full"
                        fontSize="12px"
                        fontWeight="medium"
                      >
                        No Jobs
                      </Badge>
                    )}
                  </Box>
                </HStack>
              </Flex>
            </VStack>
          </VStack>
        </Box>
      </Flex>

      {/* Detailed Information Tabs */}
      <Box
        bg="background.secondary"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        boxShadow="sm"
        _hover={{ boxShadow: 'md' }}
        transition="all 0.2s ease-in-out"
      >
        <Tabs.Root
          value={activeTab}
          onValueChange={(e) => setActiveTab(e.value)}
          colorPalette="blue"
        >
          <Box p="32px 32px 0 32px">
            <Tabs.List>
              <Tabs.Trigger value="overview" asChild>
                <Text fontSize="16px" fontWeight={activeTab === 'overview' ? 'semibold' : 'medium'}>
                  Overview
                </Text>
              </Tabs.Trigger>
              <Tabs.Trigger value="resume" asChild>
                <Text fontSize="16px" fontWeight={activeTab === 'resume' ? 'semibold' : 'medium'}>
                  Resume
                </Text>
              </Tabs.Trigger>
              <Tabs.Trigger value="interviews" asChild>
                <Text
                  fontSize="16px"
                  fontWeight={activeTab === 'interviews' ? 'semibold' : 'medium'}
                >
                  Interviews ({candidate.screenings.length})
                </Text>
              </Tabs.Trigger>
              <Tabs.Trigger value="pipeline" asChild>
                <Text fontSize="16px" fontWeight={activeTab === 'pipeline' ? 'semibold' : 'medium'}>
                  Pipeline Jobs ({candidate.pipeline_jobs.length})
                </Text>
              </Tabs.Trigger>
              {candidate.match_jobs.length > 0 && (
                <Tabs.Trigger value="matches" asChild>
                  <Text
                    fontSize="16px"
                    fontWeight={activeTab === 'matches' ? 'semibold' : 'medium'}
                  >
                    Job Matches ({candidate.match_jobs.length})
                  </Text>
                </Tabs.Trigger>
              )}
            </Tabs.List>
          </Box>

          <Box p="16px 32px">
            <Tabs.Content value="overview">
              <CandidateOverviewSection
                firstName={candidate.first_name}
                lastName={candidate.last_name}
                email={candidate.email}
                fitPositions={candidate.fit_positions}
                resume={candidate.resume_data}
              />
            </Tabs.Content>

            <Tabs.Content value="resume">
              <Flex
                w="100%"
                alignItems="center"
                justifyContent="center"
                direction="column"
                gap="16px"
              >
                <Box mr="100px" alignSelf="end">
                  {candidate.resume_type === ResumeType.Veterans ? (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          bg="transparent"
                          color="primary.900"
                          w="40px"
                          h="40px"
                          border="1px solid"
                          borderRadius="12px"
                          _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
                        >
                          <Download size={32} />
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content borderRadius="12px">
                          <Menu.Item value="original" cursor="pointer" asChild>
                            <a
                              href={candidate.resume_url}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <HStack gap="8px">
                                <Download size={16} />
                                <Text fontSize="14px">Original Resume</Text>
                              </HStack>
                            </a>
                          </Menu.Item>
                          <Menu.Item
                            value="formatted-docx"
                            onClick={handleExportFormattedResumeDocx}
                            disabled={exportingDocx}
                            cursor={exportingDocx ? 'not-allowed' : 'pointer'}
                          >
                            <HStack gap="8px">
                              <FileDoc size={16} />
                              <Text fontSize="14px">
                                {exportingDocx ? 'Exporting...' : 'Formatted Resume'}
                              </Text>
                            </HStack>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  ) : (
                    <Tooltip
                      openDelay={0}
                      closeDelay={0}
                      content="Download original resume"
                      contentProps={{
                        css: {
                          '--tooltip-bg': 'white',
                          color: 'neutrals.900',
                          fontSize: '12px'
                        }
                      }}
                    >
                      <Button
                        bg="transparent"
                        color="primary.900"
                        w="40px"
                        h="40px"
                        border="1px solid"
                        borderRadius="12px"
                        _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
                      >
                        <a
                          href={candidate.resume_url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download size={32} />
                        </a>
                      </Button>
                    </Tooltip>
                  )}
                </Box>

                <Box w="80%">
                  <Resume ref={resumeRef} resume={candidate.resume_data} />
                </Box>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="interviews">
              <InterviewAnalysisIndex
                candidateId={candidate.candidate_id}
                screenings={candidate.screenings}
              />
            </Tabs.Content>

            {candidate.match_jobs.length > 0 && (
              <Tabs.Content value="matches">
                <VStack align="flex-start" gap="24px">
                  <Text fontSize="18px" fontWeight="semibold" color="neutrals.800">
                    Matched Jobs
                  </Text>
                  <VStack align="flex-start" gap="16px" w="100%">
                    {candidate.match_jobs.map((match, index) => (
                      <Flex w="100%" gap="24px" alignItems="center">
                        <Card.Root
                          key={index}
                          bg="neutrals.50"
                          borderRadius="12px"
                          p="20px"
                          w="100%"
                          boxShadow="sm"
                          _hover={{ boxShadow: 'md', cursor: 'pointer' }}
                          transition="all 0.2s ease-in-out"
                          onClick={() => {
                            navigate(`/job/details/${match.job_id}`);
                          }}
                        >
                          <Flex align="center" gap="40px">
                            <VStack alignItems="flex-start">
                              <Text fontSize="12px" fontWeight="semibold" color="neutrals.700">
                                Match
                              </Text>
                              <Box
                                borderRadius="full"
                                p="16px"
                                w="60px"
                                h="60px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="16px"
                                fontWeight="bold"
                                bg={
                                  match.score >= 90
                                    ? 'success.300'
                                    : match.score >= 80
                                      ? 'warning.300'
                                      : 'error.300'
                                }
                              >
                                {match.score}%
                              </Box>
                            </VStack>
                            <VStack align="flex-start" gap="8px" w="100%">
                              <Flex justifyContent="space-between" alignItems="center" w="100%">
                                <Text fontSize="18px" fontWeight="semibold" color="neutrals.900">
                                  {match.job_title || 'Job Title'}
                                </Text>
                                <HStack gap="12px">
                                  <Text fontSize="14px" color="neutrals.500" fontStyle="italic">
                                    {(() => {
                                      const days = getCreatedDateFromNow(match.created_at);
                                      return days <= 0
                                        ? 'Active today'
                                        : `Active for ${days} day${days > 1 ? 's' : ''}`;
                                    })()}
                                  </Text>
                                </HStack>
                              </Flex>
                              <Text fontSize="16px" color="neutrals.700">
                                {match.client?.name || 'Company'}
                              </Text>
                            </VStack>
                          </Flex>
                        </Card.Root>
                        {candidate.pipeline_jobs.some((job) => job.job_id === match.job_id) ? (
                          <Badge
                            bg="green.100"
                            color="green.800"
                            px="12px"
                            py="6px"
                            borderRadius="8px"
                            fontSize="12px"
                            fontWeight="semibold"
                          >
                            In Pipeline
                          </Badge>
                        ) : (
                          <Tooltip
                            openDelay={0}
                            closeDelay={0}
                            content="Add to the job pipeline"
                            contentProps={{
                              css: {
                                '--tooltip-bg': 'white',
                                color: 'neutrals.900',
                                fontSize: '12px'
                              }
                            }}
                          >
                            <Button
                              size="sm"
                              bg="neutrals.0"
                              color="primary.900"
                              border="1px solid"
                              borderColor="primary.900"
                              borderRadius="full"
                              w="48px"
                              h="48px"
                              _hover={{
                                bg: 'primary.900',
                                color: 'neutrals.0'
                              }}
                              loading={addingToJob === match.job_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddCandidateToJob(match.job_id);
                              }}
                            >
                              <Plus weight="bold" size={16} />
                            </Button>
                          </Tooltip>
                        )}
                      </Flex>
                    ))}
                  </VStack>
                </VStack>
              </Tabs.Content>
            )}

            <Tabs.Content value="pipeline">
              <VStack align="flex-start" gap="24px">
                <Flex justify="space-between" align="center" w="100%">
                  <Text fontSize="18px" fontWeight="semibold" color="neutrals.800">
                    Pipeline Jobs
                  </Text>
                  <AddCandidateToJobDialog
                    candidateId={candidate.candidate_id}
                    candidateName={candidate.first_name}
                    onAddToJob={handleAddCandidateToJob}
                  />
                </Flex>
                {candidate.pipeline_jobs.length > 0 ? (
                  <VStack align="flex-start" gap="16px" w="100%">
                    {candidate.pipeline_jobs.map((job, index) => {
                      const clickable =
                        job.candidate_status !== JobCandidateStatus.New &&
                        job.candidate_status !== JobCandidateStatus.Fatal;

                      return (
                        <Card.Root
                          key={index}
                          bg="neutrals.50"
                          borderRadius="12px"
                          p="20px"
                          w="90%"
                          boxShadow="sm"
                          _hover={{
                            boxShadow: clickable ? 'md' : 'sm',
                            cursor: clickable ? 'pointer' : 'default'
                          }}
                          transition="all 0.2s ease-in-out"
                          onClick={() => {
                            if (clickable) {
                              navigate(`/job/details/${job.job_id}/candidate/${candidateId}`);
                            }
                          }}
                        >
                          <Flex align="center" gap="40px">
                            <VStack alignItems="flex-start">
                              <Text fontSize="12px" fontWeight="semibold" color="neutrals.700">
                                Score
                              </Text>
                              <Box
                                borderRadius="full"
                                p="16px"
                                w="60px"
                                h="60px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="16px"
                                fontWeight="bold"
                                bg={
                                  job.candidate_status === JobCandidateStatus.New ||
                                  job.candidate_status === JobCandidateStatus.Fatal
                                    ? 'neutrals.300'
                                    : job.overall_score >= 90
                                      ? 'success.300'
                                      : job.overall_score >= 80
                                        ? 'warning.300'
                                        : 'error.300'
                                }
                              >
                                {job.candidate_status === JobCandidateStatus.New ? (
                                  <HourglassHigh size={20} />
                                ) : job.candidate_status === JobCandidateStatus.Fatal ? (
                                  <Warning size={20} />
                                ) : (
                                  job.overall_score
                                )}
                              </Box>
                            </VStack>
                            <VStack align="flex-start" gap="8px" w="100%">
                              <Flex justifyContent="space-between" alignItems="center" w="100%">
                                <Badge
                                  bg="primary.100"
                                  color="primary.900"
                                  px="12px"
                                  py="4px"
                                  borderRadius="full"
                                  fontSize="12px"
                                  fontWeight="semibold"
                                >
                                  {job.candidate_status === JobCandidateStatus.New
                                    ? 'Analyzing...'
                                    : getJobCandidateStatusLabel(job.candidate_status)}
                                </Badge>
                                <Text fontSize="14px" color="neutrals.500" fontStyle="italic">
                                  {(() => {
                                    const days = getCreatedDateFromNow(job.created_at);
                                    return days <= 0
                                      ? 'Active today'
                                      : `Active for ${days} day${days > 1 ? 's' : ''}`;
                                  })()}
                                </Text>
                              </Flex>
                              <Text fontSize="18px" fontWeight="semibold" color="neutrals.900">
                                {job.job_title || 'Job Title'}
                              </Text>
                              <Text fontSize="16px" color="neutrals.700">
                                {job.client?.name || 'Company'}
                              </Text>
                            </VStack>
                          </Flex>
                        </Card.Root>
                      );
                    })}
                  </VStack>
                ) : (
                  <Flex
                    direction="column"
                    justify="center"
                    align="center"
                    py="60px"
                    w="100%"
                    mb="40px"
                  >
                    <Text fontSize="20px" fontWeight="medium" color="neutrals.600" mb="8px">
                      No pipeline jobs yet
                    </Text>
                    <Text fontSize="16px" color="neutrals.500" textAlign="center">
                      Add this candidate to a job pipeline to start tracking their progress
                    </Text>
                  </Flex>
                )}
              </VStack>
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Box>
    </Flex>
  );
}
